import { apiFetch } from "./apiClient";

const BASE_PATH = "/api/services/jobs";

function toQueryString(filters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
      return;
    }

    params.append(key, value);
  });

  const query = params.toString();
  return query ? `?${query}` : "";
}

async function parseResponse(res, fallbackErrorMessage) {
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json() : null;

  if (!res.ok) {
    const err = new Error(data?.error || fallbackErrorMessage);
    if (data?.code) {
      err.code = data.code;
    }
    throw err;
  }

  return data;
}

export async function getAssignmentPeriods(filters = {}) {
  const queryString = toQueryString({
    assigned_user_id: filters.assigned_user_id,
    period_start: filters.period_start,
    period_end: filters.period_end,
    status: filters.status,
    label: filters.label,
    include_draft: filters.include_draft,
    customer_id: filters.customer_id,
  });
  const res = await apiFetch(`${BASE_PATH}/assignment-periods${queryString}`);
  return parseResponse(res, "Failed to fetch assignment periods");
}

export async function getAssignmentPeriodJobs(periodId, options = {}) {
  const queryString = toQueryString({
    include_draft: options.include_draft,
  });

  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/${periodId}/jobs${queryString}`,
  );
  return parseResponse(res, "Failed to fetch assignment period jobs");
}

export async function getAssignmentPeriodsByJob(jobId, options = {}) {
  const queryString = toQueryString({
    include_draft: options.include_draft,
  });

  const res = await apiFetch(
    `${BASE_PATH}/${jobId}/assignment-periods${queryString}`,
  );
  return parseResponse(res, "Failed to fetch job assignment periods");
}

export async function createAssignmentPeriod(payload) {
  const res = await apiFetch(`${BASE_PATH}/assignment-periods`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return parseResponse(res, "Failed to create assignment period");
}

export async function updateAssignmentPeriod(periodId, payload) {
  const res = await apiFetch(`${BASE_PATH}/assignment-periods/${periodId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

  return parseResponse(res, "Failed to update assignment period");
}

export async function estimateAssignmentPeriodEndTime(payload) {
  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/estimate-end-time`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  return parseResponse(res, "Failed to estimate assignment period end time");
}

export async function deleteAssignmentPeriod(periodId) {
  const res = await apiFetch(`${BASE_PATH}/assignment-periods/${periodId}`, {
    method: "DELETE",
  });

  return parseResponse(res, "Failed to delete assignment period");
}

export async function addJobToAssignmentPeriod(periodId, payload) {
  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/${periodId}/jobs`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  return parseResponse(res, "Failed to add job to assignment period");
}

export async function addJobsToAssignmentPeriod(
  periodId,
  jobIds,
  options = {},
) {
  return addJobToAssignmentPeriod(periodId, {
    job_ids: jobIds,
    sync_assignments: options.sync_assignments,
  });
}

export async function reorderAssignmentPeriodJobs(periodId, orderedJobIds) {
  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/${periodId}/jobs/order`,
    {
      method: "PUT",
      body: JSON.stringify({ ordered_job_ids: orderedJobIds }),
    },
  );

  return parseResponse(res, "Failed to reorder assignment period jobs");
}

export async function removeJobFromAssignmentPeriod(
  periodId,
  jobId,
  options = {},
) {
  const queryString = toQueryString({
    resequence: options.resequence,
  });

  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/${periodId}/jobs/${jobId}${queryString}`,
    {
      method: "DELETE",
    },
  );

  return parseResponse(res, "Failed to remove job from assignment period");
}

export async function mergeAssignmentPeriods(periodIds, targetAssignedUserId) {
  const res = await apiFetch(`${BASE_PATH}/assignment-periods/merge`, {
    method: "POST",
    body: JSON.stringify({
      period_ids: periodIds,
      target_assigned_user_id:
        targetAssignedUserId === undefined ? null : targetAssignedUserId,
    }),
  });

  return parseResponse(res, "Failed to merge assignment periods");
}

export async function syncAssignmentPeriod(periodId) {
  const res = await apiFetch(
    `${BASE_PATH}/assignment-periods/${periodId}/sync-assignment`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );

  return parseResponse(res, "Failed to sync assignment period");
}
